import { colors } from '@apollo/brand';
import { customColors } from '@apollo/orbit';
import { useMemo } from 'react';

import { useStackInfo } from 'src/components/charts/helpers/useStackInfo';
import { LineChartStackConfig } from 'src/components/charts/LineChart';
import { date } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';

import { OperationMetricType } from './types';

export function useServiceReportLineCharts(
  requestsChartData: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats[],
  eventsChartData: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats[],
  resolution: GraphQLTypes.Resolution,
  isSubscriptionOperation: boolean,
  operationMetricType: OperationMetricType,
) {
  const requestsLineChartStacks = useMemo(
    (): LineChartStackConfig<GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats>[] => [
      {
        colorGradientTop: `${colors.primitives.blue[300]}3C`,
        colorGradientBottom: `${colors.primitives.blue[300]}00`,
        primaryColor: customColors.flowchart.d,
        cursorColor: customColors.brand.accent,
        label: isSubscriptionOperation ? 'Registrations' : 'Requests',
        unit: operationMetricType === 'requestRate' ? '/min' : undefined,
        data: requestsChartData,
        hasData: (
          stats: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats,
        ) => stats.metrics.uncachedHistogram.totalCount > 0,
        getValue: (
          stats: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats,
        ) => ({
          timestamp: stats.timestamp,
          value:
            operationMetricType === 'requestRate'
              ? stats.metrics.uncachedHistogram.totalCount /
                date.getMinDifference(
                  stats.timestamp,
                  date.incrementByResolution(stats.timestamp, resolution),
                )
              : stats.metrics.uncachedHistogram.totalCount,
        }),
      },
      {
        colorGradientTop: `${colors.primitives.yellow[300]}3C`,
        colorGradientBottom: `${colors.primitives.yellow[300]}00`,
        primaryColor: customColors.flowchart.a,
        cursorColor: customColors.brand.accent,
        label: 'Cache hits',
        unit: operationMetricType === 'requestRate' ? '/min' : undefined,
        data: requestsChartData,
        hasData: (
          stats: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats,
        ) => stats.metrics.cachedHistogram.totalCount > 0,
        getValue: (
          stats: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats,
        ) => ({
          timestamp: stats.timestamp,
          value:
            operationMetricType === 'requestRate'
              ? stats.metrics.cachedHistogram.totalCount /
                date.getMinDifference(
                  stats.timestamp,
                  date.incrementByResolution(stats.timestamp, resolution),
                )
              : stats.metrics.cachedHistogram.totalCount,
        }),
      },
    ],
    [
      isSubscriptionOperation,
      operationMetricType,
      requestsChartData,
      resolution,
    ],
  );

  const eventsLineChartStacks = useMemo(
    (): LineChartStackConfig<GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats>[] => [
      {
        colorGradientTop: `${colors.primitives.purple[300]}3C`,
        colorGradientBottom: `${colors.primitives.purple[300]}00`,
        primaryColor: customColors.border.beta,
        cursorColor: customColors.brand.accent,
        label: 'Notifications',
        unit: '/min',
        data: eventsChartData,
        hasData: (
          stats: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats,
        ) => stats.metrics.uncachedHistogram.totalCount > 0,
        getValue: (
          stats: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats,
        ) => ({
          timestamp: stats.timestamp,
          value:
            stats.metrics.uncachedHistogram.totalCount /
            date.getMinDifference(
              stats.timestamp,
              date.incrementByResolution(stats.timestamp, resolution),
            ),
        }),
      },
      {
        colorGradientTop: `${colors.primitives.yellow[300]}3C`,
        colorGradientBottom: `${colors.primitives.yellow[300]}00`,
        primaryColor: customColors.flowchart.a,
        cursorColor: customColors.brand.accent,
        label: 'Cache hits',
        unit: '/min',
        data: eventsChartData,
        hasData: (
          stats: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats,
        ) => stats.metrics.cachedHistogram.totalCount > 0,
        getValue: (
          stats: GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats,
        ) => ({
          timestamp: stats.timestamp,
          value:
            stats.metrics.cachedHistogram.totalCount /
            date.getMinDifference(
              stats.timestamp,
              date.incrementByResolution(stats.timestamp, resolution),
            ),
        }),
      },
    ],
    [eventsChartData, resolution],
  );

  const requestsLineChartStackInfo = useStackInfo<
    GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats,
    { timestamp: Timestamp['output']; value: number },
    LineChartStackConfig<GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats>
  >(requestsLineChartStacks);

  const eventsLineChartStackInfo = useStackInfo<
    GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats,
    { timestamp: Timestamp['output']; value: number },
    LineChartStackConfig<GraphQLTypes.UI__ServiceReportQuery_service_stats_queryStats>
  >(eventsLineChartStacks);

  return {
    requestsLineChartStackInfo,
    eventsLineChartStackInfo,
  };
}
